
import { AnalyticsRepository, AccountContext } from '../repository/analytics.repository';
import { IAnalyticsUseCase } from './interfaces';

/**
 * Analytics Use Case
 * Handles business logic for analytics, reporting, and activity tracking
 */
export class AnalyticsUseCase  implements IAnalyticsUseCase {
  constructor(private analyticsRepo: AnalyticsRepository) {
    // Removed BaseUseCase
  }

  /**
   * Track user activity and generate insights
   */
  async trackUserActivity(
    userId: string,
    activity: {
      type: 'login' | 'courseView' | 'lessonComplete' | 'quizAttempt' | 'enrollment' | 'purchase';
      entityId?: string;
      entityType?: string;
      metadata?: Record<string, any>;
    },
    context?: AccountContext
  ): Promise<void> {
    try {
      // Track analytics event
      await this.analyticsRepo.trackAnalyticsEvent(
        `user_${activity.type}`,
        {
          userId: userId,
          entityId: activity.entityId,
          entityType: activity.entityType,
          ...activity.metadata,
        },
        userId,
        context
      );

      // Track activity log
      if (activity.entityId && activity.entityType) {
        await this.analyticsRepo.trackEntityActivity(
          activity.entityType,
          activity.entityId,
          'view', // Default activity type
          userId,
          {
            activityType: activity.type,
            ...activity.metadata,
          },
          context
        );
      }
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Track course enrollment analytics
   */
  async trackCourseEnrollment(
    enrollmentData: {
      userId: string;
      courseId: string;
      enrollmentType: 'paid' | 'free' | 'trial';
      price?: number;
    },
    context?: AccountContext
  ): Promise<void> {
    try {
      // Track enrollment event
      await this.analyticsRepo.trackAnalyticsEvent(
        'enrollmentCreated',
        {
          userId: enrollmentData.userId,
          courseId: enrollmentData.courseId,
          enrollmentType: enrollmentData.enrollmentType,
          price: enrollmentData.price,
        },
        enrollmentData.userId,
        context
      );

      // Track activity
      await this.analyticsRepo.trackEntityActivity(
        'enrollment',
        `${enrollmentData.userId}_${enrollmentData.courseId}`,
        'enroll',
        enrollmentData.userId,
        {
          courseId: enrollmentData.courseId,
          enrollmentType: enrollmentData.enrollmentType,
          price: enrollmentData.price,
        },
        context
      );

      // Update enrollment metrics
      await this.analyticsRepo.createMetric({
        metricType: 'enrollmentCount',
        metricValue: 1,
        dimensions: {
          courseId: enrollmentData.courseId,
          enrollmentType: enrollmentData.enrollmentType,
        },
        timestamp: new Date(),
      }, context);

      // Update revenue metrics if paid
      if (enrollmentData.price && enrollmentData.price > 0) {
        await this.analyticsRepo.createMetric({
          metricType: 'revenue',
          metricValue: enrollmentData.price,
          dimensions: {
            courseId: enrollmentData.courseId,
            revenueType: 'enrollment',
          },
          timestamp: new Date(),
        }, context);
      }
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Track lesson completion and learning progress
   */
  async trackLessonCompletion(
    completionData: {
      userId: string;
      lessonId: string;
      courseId: string;
      timeSpentSeconds: number;
      completionPercentage: number;
    },
    context?: AccountContext
  ): Promise<void> {
    try {
      // Track lesson completion event
      await this.analyticsRepo.trackAnalyticsEvent(
        'lessonCompleted',
        {
          userId: completionData.userId,
          lessonId: completionData.lessonId,
          courseId: completionData.courseId,
          timeSpentSeconds: completionData.timeSpentSeconds,
          completionPercentage: completionData.completionPercentage,
        },
        completionData.userId,
        context
      );

      // Track activity
      await this.analyticsRepo.trackEntityActivity(
        'lesson',
        completionData.lessonId,
        'complete',
        completionData.userId,
        {
          courseId: completionData.courseId,
          timeSpentSeconds: completionData.timeSpentSeconds,
          completionPercentage: completionData.completionPercentage,
        },
        context
      );

      // Update learning metrics
      if (completionData.completionPercentage === 100) {
        await this.analyticsRepo.createMetric({
          metricType: 'completionRate',
          metricValue: 1,
          dimensions: {
            courseId: completionData.courseId,
            lessonId: completionData.lessonId,
            completionType: 'lesson',
          },
          timestamp: new Date(),
        }, context);
      }
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Track quiz attempts and performance
   */
  async trackQuizAttempt(
    attemptData: {
      userId: string;
      quizId: string;
      courseId: string;
      score: number;
      totalQuestions: number;
      timeSpentSeconds: number;
      passed: boolean;
    },
    context?: AccountContext
  ): Promise<void> {
    try {
      // Track quiz attempt event
      await this.analyticsRepo.trackAnalyticsEvent(
        'quizAttempted',
        {
          userId: attemptData.userId,
          quizId: attemptData.quizId,
          courseId: attemptData.courseId,
          score: attemptData.score,
          totalQuestions: attemptData.totalQuestions,
          timeSpentSeconds: attemptData.timeSpentSeconds,
          passed: attemptData.passed,
        },
        attemptData.userId,
        context
      );

      // Track activity
      await this.analyticsRepo.trackEntityActivity(
        'quiz',
        attemptData.quizId,
        'complete',
        attemptData.userId,
        {
          courseId: attemptData.courseId,
          score: attemptData.score,
          totalQuestions: attemptData.totalQuestions,
          passed: attemptData.passed,
        },
        context
      );

      // Track completion if passed
      if (attemptData.passed) {
        await this.analyticsRepo.trackAnalyticsEvent(
          'quizCompleted',
          {
            userId: attemptData.userId,
            quizId: attemptData.quizId,
            courseId: attemptData.courseId,
            score: attemptData.score,
          },
          attemptData.userId,
          context
        );

        // Update completion metrics
        await this.analyticsRepo.createMetric({
          metricType: 'completionRate',
          metricValue: 1,
          dimensions: {
            courseId: attemptData.courseId,
            quizId: attemptData.quizId,
            completionType: 'quiz',
          },
          timestamp: new Date(),
        }, context);
      }
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Track resource access and engagement
   */
  async trackResourceAccess(
    accessData: {
      userId: string;
      resourceId: string;
      resourceType: string;
      accessType: 'view' | 'download' | 'purchase';
      timeSpentSeconds?: number;
    },
    context?: AccountContext
  ): Promise<void> {
    try {
      // Track resource access event
      await this.analyticsRepo.trackAnalyticsEvent(
        'resourceViewed',
        {
          userId: accessData.userId,
          resourceId: accessData.resourceId,
          resourceType: accessData.resourceType,
          accessType: accessData.accessType,
          timeSpentSeconds: accessData.timeSpentSeconds,
        },
        accessData.userId,
        context
      );

      // Track activity
      await this.analyticsRepo.trackEntityActivity(
        'resource',
        accessData.resourceId,
        accessData.accessType,
        accessData.userId,
        {
          resourceType: accessData.resourceType,
          timeSpentSeconds: accessData.timeSpentSeconds,
        },
        context
      );

      // Track specific metrics based on access type
      if (accessData.accessType === 'download') {
        await this.analyticsRepo.trackAnalyticsEvent(
          'resourceDownloaded',
          {
            userId: accessData.userId,
            resourceId: accessData.resourceId,
            resourceType: accessData.resourceType,
          },
          accessData.userId,
          context
        );
      }

      if (accessData.accessType === 'purchase') {
        await this.analyticsRepo.trackAnalyticsEvent(
          'resourcePurchase',
          {
            userId: accessData.userId,
            resourceId: accessData.resourceId,
            resourceType: accessData.resourceType,
          },
          accessData.userId,
          context
        );
      }
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Generate user learning analytics
   */
  async getUserLearningAnalytics(
    userId: string,
    dateRange?: { start: Date; end: Date },
    context?: AccountContext
  ): Promise<{
    coursesEnrolled: number;
    coursesCompleted: number;
    lessonsCompleted: number;
    quizzesAttempted: number;
    quizzesPassed: number;
    totalTimeSpent: number;
    averageScore: number;
    learningStreak: number;
    recentActivity: any[];
  }> {
    try {
      // Get user activity summary
      const activitySummary = await this.analyticsRepo.getUserActivitySummary(
        userId,
        dateRange,
        context
      );

      // Get learning-specific events
      const learningEvents = await this.analyticsRepo.getAnalyticsEvents({
        userId: userId,
        dateFrom: dateRange?.start,
        dateTo: dateRange?.end,
      }, undefined, context);

      // Calculate learning metrics
      const enrollmentEvents = learningEvents.filter(e => e.eventType === 'enrollmentCreated');
      const completionEvents = learningEvents.filter(e =>
        e.eventType === 'lessonCompleted' || e.eventType === 'quizCompleted'
      );
      const lessonEvents = learningEvents.filter(e => e.eventType === 'lessonCompleted');
      const quizEvents = learningEvents.filter(e =>
        e.eventType === 'quizAttempted' || e.eventType === 'quizCompleted'
      );

      const coursesEnrolled = enrollmentEvents.length;
      const lessonsCompleted = lessonEvents.length;
      const quizzesAttempted = quizEvents.length;
      const quizzesPassed = quizEvents.filter(e =>
        e.eventData?.passed === true
      ).length;

      // Calculate total time spent
      const totalTimeSpent = learningEvents.reduce((total, event) => {
        return total + (event.eventData?.timeSpentSeconds || 0);
      }, 0);

      // Calculate average score
      const scoredEvents = quizEvents.filter(e => e.eventData?.score !== undefined);
      const averageScore = scoredEvents.length > 0
        ? scoredEvents.reduce((sum, event) => sum + (event.eventData?.score || 0), 0) / scoredEvents.length
        : 0;

      // Mock learning streak (would need more complex logic)
      const learningStreak = Math.floor(Math.random() * 30) + 1;

      // Get recent activity
      const recentActivity = learningEvents
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 10);

      return {
        coursesEnrolled,
        coursesCompleted: Math.floor(coursesEnrolled * 0.7), // Mock completion rate
        lessonsCompleted,
        quizzesAttempted,
        quizzesPassed,
        totalTimeSpent,
        averageScore: Math.round(averageScore * 100) / 100,
        learningStreak,
        recentActivity,
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Generate instructor/course analytics
   */
  async getInstructorAnalytics(
    instructorId: string,
    dateRange?: { start: Date; end: Date },
    context?: AccountContext
  ): Promise<{
    totalCourses: number;
    totalStudents: number;
    totalRevenue: number;
    averageRating: number;
    coursePerformance: Array<{
      courseId: string;
      title: string;
      enrollments: number;
      completions: number;
      revenue: number;
      rating: number;
    }>;
    studentEngagement: {
      averageCompletionRate: number;
      averageTimeSpent: number;
      mostPopularCourse: string;
    };
  }> {
    try {
      // Get instructor's course creation events
      const courseEvents = await this.analyticsRepo.getAnalyticsEvents({
        userId: instructorId,
        eventType: 'courseCreated',
        dateFrom: dateRange?.start,
        dateTo: dateRange?.end,
      }, undefined, context);

      const totalCourses = courseEvents.length;

      // Mock instructor analytics (would need to aggregate from multiple sources)
      const totalStudents = Math.floor(Math.random() * 500) + 50;
      const totalRevenue = Math.floor(Math.random() * 10000) + 1000;
      const averageRating = Math.round((Math.random() * 2 + 3) * 10) / 10; // 3.0 to 5.0

      // Mock course performance data
      const coursePerformance = courseEvents.map((event, index) => ({
        courseId: event.eventData?.courseId || `course_${index}`,
        title: event.eventData?.title || `Course ${index + 1}`,
        enrollments: Math.floor(Math.random() * 50) + 10,
        completions: Math.floor(Math.random() * 30) + 5,
        revenue: Math.floor(Math.random() * 1000) + 100,
        rating: Math.round((Math.random() * 2 + 3) * 10) / 10,
      }));

      const studentEngagement = {
        averageCompletionRate: Math.round((Math.random() * 30 + 50) * 100) / 100, // 50-80%
        averageTimeSpent: Math.floor(Math.random() * 3600) + 1800, // 30-90 minutes
        mostPopularCourse: coursePerformance[0]?.title || 'N/A',
      };

      return {
        totalCourses,
        totalStudents,
        totalRevenue,
        averageRating,
        coursePerformance,
        studentEngagement,
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Generate platform-wide analytics
   */
  async getPlatformAnalytics(
    dateRange?: { start: Date; end: Date },
    context?: AccountContext
  ): Promise<{
    overview: {
      totalUsers: number;
      totalCourses: number;
      totalEnrollments: number;
      totalRevenue: number;
      activeUsers: number;
    };
    growth: {
      userGrowth: number;
      revenueGrowth: number;
      courseGrowth: number;
    };
    engagement: {
      averageSessionDuration: number;
      courseCompletionRate: number;
      userRetentionRate: number;
    };
    topPerformers: {
      courses: Array<{ id: string; title: string; enrollments: number }>;
      instructors: Array<{ id: string; name: string; revenue: number }>;
    };
  }> {
    try {
      // Get platform analytics from repository
      const platformData = await this.analyticsRepo.getPlatformAnalytics(dateRange, context);

      // Calculate growth metrics (mock)
      const growth = {
        userGrowth: Math.round((Math.random() * 20 + 5) * 100) / 100, // 5-25%
        revenueGrowth: Math.round((Math.random() * 30 + 10) * 100) / 100, // 10-40%
        courseGrowth: Math.round((Math.random() * 15 + 5) * 100) / 100, // 5-20%
      };

      // Calculate engagement metrics (mock)
      const engagement = {
        averageSessionDuration: Math.floor(Math.random() * 1800) + 600, // 10-40 minutes
        courseCompletionRate: Math.round((Math.random() * 20 + 60) * 100) / 100, // 60-80%
        userRetentionRate: Math.round((Math.random() * 15 + 70) * 100) / 100, // 70-85%
      };

      // Mock top performers
      const topPerformers = {
        courses: [
          { id: 'course1', title: 'Popular Course 1', enrollments: 150 },
          { id: 'course2', title: 'Popular Course 2', enrollments: 120 },
          { id: 'course3', title: 'Popular Course 3', enrollments: 95 },
        ],
        instructors: [
          { id: 'inst1', name: 'Instructor A', revenue: 5000 },
          { id: 'inst2', name: 'Instructor B', revenue: 4200 },
          { id: 'inst3', name: 'Instructor C', revenue: 3800 },
        ],
      };

      return {
        overview: {
          totalUsers: platformData.totalUsers,
          totalCourses: platformData.totalCourses,
          totalEnrollments: platformData.totalEnrollments,
          totalRevenue: platformData.totalRevenue,
          activeUsers: Math.floor(platformData.totalUsers * 0.7), // 70% active
        },
        growth,
        engagement,
        topPerformers,
      };
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  /**
   * Export analytics data for reporting
   */
  async exportAnalyticsData(
    exportConfig: {
      dataTypes: ('events' | 'activities' | 'metrics')[];
      dateRange?: { start: Date; end: Date };
      filters?: Record<string, any>;
      format: 'json' | 'csv';
    },
    context?: AccountContext
  ): Promise<{
    data: any;
    format: string;
    recordCount: number;
    exportTimestamp: Date;
  }> {
    try {
      const exportData: any = {};
      let totalRecords = 0;

      // Export events
      if (exportConfig.dataTypes.includes('events')) {
        const events = await this.analyticsRepo.getAnalyticsEvents({
          dateFrom: exportConfig.dateRange?.start,
          dateTo: exportConfig.dateRange?.end,
          ...exportConfig.filters,
        }, undefined, context);
        exportData.events = events;
        totalRecords += events.length;
      }

      // Export activities
      if (exportConfig.dataTypes.includes('activities')) {
        const activities = await this.analyticsRepo.getActivityLogs({
          dateFrom: exportConfig.dateRange?.start,
          dateTo: exportConfig.dateRange?.end,
          ...exportConfig.filters,
        }, undefined, context);
        exportData.activities = activities;
        totalRecords += activities.length;
      }

      // Export metrics
      if (exportConfig.dataTypes.includes('metrics')) {
        const metrics = await this.analyticsRepo.getMetrics({
          dateFrom: exportConfig.dateRange?.start,
          dateTo: exportConfig.dateRange?.end,
          ...exportConfig.filters,
        }, undefined, context);
        exportData.metrics = metrics;
        totalRecords += metrics.length;
      }

      return {
        data: exportData,
        format: exportConfig.format,
        recordCount: totalRecords,
        exportTimestamp: new Date(),
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }
}
